import { useEffect, useState } from 'react';
import { Outlet, Link as RouterLink, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  Button,
  Container,
  Drawer,
  Grid,
  IconButton,
  Link,
  Stack,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import BrandMark from '../components/BrandMark';
import { useAuth } from '../contexts/AuthContext';
import { trackCta } from '../lib/analytics';

const navLinks = [
  { to: '/tools', label: 'Free tools' },
  { to: '/compare', label: 'vs alternatives' },
  { to: '/blog', label: 'Blog' },
];

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign'] as const;
const UTM_STORAGE_KEY = 'tf.utm_attribution';

function captureUtmsToSession() {
  try {
    const params = new URLSearchParams(window.location.search);
    const captured: Record<string, string> = {};
    for (const k of UTM_KEYS) {
      const v = params.get(k);
      if (v) captured[k] = v;
    }
    if (Object.keys(captured).length === 0) return;
    if (window.sessionStorage.getItem(UTM_STORAGE_KEY)) return;
    window.sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(captured));
  } catch {
    // sessionStorage disabled — no-op.
  }
}

export default function MarketingShell() {
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'));
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const location = useLocation();

  useEffect(() => {
    captureUtmsToSession();
  }, []);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', flexDirection: 'column' }}>
      <AppBar
        position="sticky"
        elevation={0}
        color="transparent"
        sx={{
          backdropFilter: 'saturate(180%) blur(8px)',
          bgcolor: 'rgba(255,255,255,0.85)',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ minHeight: { xs: 60, md: 68 } }}>
            <BrandMark size={32} />
            <Box sx={{ flex: 1 }} />
            {isMd && (
              <Stack direction="row" spacing={3.5} sx={{ mr: 3 }} component="nav" aria-label="Main">
                {navLinks.map((l) => (
                  <Link
                    key={l.to}
                    component={RouterLink}
                    to={l.to}
                    underline="none"
                    sx={{
                      fontSize: 14,
                      fontWeight: 500,
                      color: location.pathname.startsWith(l.to) ? 'text.primary' : 'text.secondary',
                      '&:hover': { color: 'text.primary' },
                    }}
                  >
                    {l.label}
                  </Link>
                ))}
              </Stack>
            )}
            <Stack direction="row" spacing={1.5} alignItems="center">
              {user ? (
                <Button
                  component={RouterLink}
                  to="/dashboard"
                  variant="contained"
                  size="small"
                  aria-label="Go to dashboard"
                  sx={{ px: 2 }}
                >
                  Open dashboard
                </Button>
              ) : (
                <>
                  <Button
                    component={RouterLink}
                    to="/login"
                    variant="text"
                    size="small"
                    sx={{ color: 'text.secondary', display: { xs: 'none', sm: 'inline-flex' } }}
                    aria-label="Sign in"
                  >
                    Sign in
                  </Button>
                  <Button
                    component={RouterLink}
                    to="/#waitlist"
                    variant="contained"
                    size="small"
                    aria-label="Get early access"
                    onClick={() => trackCta('Waitlist', 'nav')}
                    sx={{ px: 2 }}
                  >
                    Get early access
                  </Button>
                </>
              )}
              {!isMd && (
                <IconButton
                  edge="end"
                  onClick={() => setOpen(true)}
                  aria-label="Open menu"
                  sx={{ ml: 0.5 }}
                >
                  <MenuIcon />
                </IconButton>
              )}
            </Stack>
          </Toolbar>
        </Container>
      </AppBar>

      <Drawer anchor="right" open={open} onClose={() => setOpen(false)}>
        <Box sx={{ width: 260, p: 2.5 }} role="presentation">
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <BrandMark size={28} />
            <IconButton onClick={() => setOpen(false)} aria-label="Close menu"><CloseIcon /></IconButton>
          </Stack>
          <Stack spacing={1.5}>
            {navLinks.map((l) => (
              <Button
                key={l.to}
                component={RouterLink}
                to={l.to}
                onClick={() => setOpen(false)}
                fullWidth
                sx={{ justifyContent: 'flex-start' }}
              >
                {l.label}
              </Button>
            ))}
            <Button
              component={RouterLink}
              to={user ? '/dashboard' : '/login'}
              onClick={() => setOpen(false)}
              fullWidth
              sx={{ justifyContent: 'flex-start' }}
            >
              {user ? 'Open dashboard' : 'Sign in'}
            </Button>
          </Stack>
        </Box>
      </Drawer>

      <Box component="main" sx={{ flex: 1 }}>
        <Outlet />
      </Box>

      <Box component="footer" sx={{ borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', mt: 12 }}>
        <Container maxWidth="lg" sx={{ py: 6 }}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={5}>
              <BrandMark size={32} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, maxWidth: 360 }}>
                The track & field workspace for coaches, athletes, and parents.
                Pass the schedule from coach to parent in one click.
              </Typography>
            </Grid>
            <Grid item xs={6} md={2}>
              <FooterColumn
                title="Product"
                items={[
                  { label: 'Features', to: '/#features' },
                  { label: 'vs alternatives', to: '/compare' },
                  { label: 'Sign in', to: '/login' },
                  {
                    label: 'Get early access',
                    to: '/#waitlist',
                    onClick: () => trackCta('Waitlist', 'footer'),
                  },
                ]}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <FooterColumn
                title="Free tools"
                items={[
                  { label: 'All tools', to: '/tools' },
                  { label: 'Heat seeder', to: '/tools/heat-seeder' },
                  { label: 'Blog', to: '/blog' },
                ]}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FooterColumn
                title="Company"
                items={[
                  { label: 'Contact', to: '/contact' },
                  { label: 'Privacy', to: '/privacy' },
                  { label: 'Terms', to: '/terms' },
                ]}
              />
            </Grid>
          </Grid>
          <Typography
            variant="caption"
            sx={{ display: 'block', mt: 5, pt: 2.5, borderTop: '1px solid', borderColor: 'divider', color: 'text.secondary', textAlign: 'center' }}
          >
            © {new Date().getFullYear()} Baton. All rights reserved.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
}

function FooterColumn({
  title,
  items,
}: {
  title: string;
  items: { label: string; to: string; external?: boolean; onClick?: () => void }[];
}) {
  return (
    <Box>
      <Typography
        variant="overline"
        sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 0.6 }}
      >
        {title}
      </Typography>
      <Stack spacing={1} sx={{ mt: 1.5 }}>
        {items.map((it) => (
          <Link
            key={it.label}
            component={it.external ? 'a' : RouterLink}
            href={it.external ? it.to : undefined}
            to={it.external ? undefined : it.to}
            onClick={it.onClick}
            underline="none"
            sx={{ fontSize: 14, color: 'text.secondary', '&:hover': { color: 'text.primary' } }}
          >
            {it.label}
          </Link>
        ))}
      </Stack>
    </Box>
  );
}
